import { createContext, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useHttpClient } from '../client/HttpClientContext';
import { useRpcClient } from '../client/useRpcClient';
import { getOwnProperty } from '../utils/getOwnProperty';
import { fetchPropertyValue } from './crmObjectsAPI';
import { makeIdentifierKey, parseIdentifierKey } from './identifierKey';
import { loadProcessingMetadata, persistProcessingMetadata } from './persistenceUtils';
import { NO_STATUS, pollSmartPropertyStatuses } from './pollingAPI';
import { usePollListeners } from './usePollListeners';
import { jsx as _jsx } from "react/jsx-runtime";
const UNSET = Symbol('UNSET');

// A structure to store values that override the controlled value. Values should
// be dropped from here when the controlled value is updated to something that
// doesn't match the stored controlled value.
export const AIPropertiesProcessingContext = /*#__PURE__*/createContext(null);
function processingMetadataReducer(metadata, action) {
  if (action.type === 'loadMetadata') {
    const {
      persistedMetadata
    } = action;
    return Object.assign({}, persistedMetadata, metadata);
  } else if (action.type === 'startProcessing') {
    // Set pending status for given property value coordinates
    const newState = structuredClone(metadata);
    const {
      identifierKeys,
      startedAt
    } = action;
    for (const key of identifierKeys) {
      newState[key] = {
        status: 'pending',
        isLoadingNewValue: false,
        startedAt
      };
    }
    return newState;
  } else if (action.type === 'handlePollingResult') {
    // Handle result from polling API and update statuses based on result
    const newState = structuredClone(metadata);
    const {
      data
    } = action;
    for (const [key, status] of Object.entries(data)) {
      if (Object.prototype.hasOwnProperty.call(newState, key)) {
        if (status === NO_STATUS) {
          // If the polling API returns nothing for coordinates we asked for,
          // the backend isn't tracking it so the frontend will also drop the
          // metadata
          delete newState[key];
        } else {
          newState[key].status = status;
          if (status === 'completed') {
            // If the API returns a completed status, we stop polling and start
            // loading the new value
            newState[key].isLoadingNewValue = true;
          }
        }
      }
    }
    return newState;
  } else if (action.type === 'finishLoadingNewValue') {
    // Handle the load of the new value after polling and value fetch has completed
    const newState = structuredClone(metadata);
    const {
      identifierKey
    } = action;
    if (Object.prototype.hasOwnProperty.call(newState, identifierKey)) {
      newState[identifierKey].isLoadingNewValue = false;
    }
    return newState;
  }
  return metadata;
}
function AIPropertiesProcessingContextProvider({
  children
}) {
  const httpClient = useHttpClient();
  const rpcClient = useRpcClient();
  const [metadata, metadataDispatch] = useReducer(processingMetadataReducer, {}, () => loadProcessingMetadata());
  const propertyValueRegistryRef = useRef({});
  const latestPollRef = useRef(0);
  const {
    startPollingAndAttach,
    detachPolling
  } = usePollListeners(keys => {
    latestPollRef.current += 1;
    const pollId = latestPollRef.current;
    pollSmartPropertyStatuses(rpcClient, keys).then(data => {
      if (latestPollRef.current !== pollId) {
        return;
      }
      metadataDispatch({
        type: 'handlePollingResult',
        data
      });
    }).catch(() => {});
  });
  useEffect(() => {
    // Get all keys that are pending a new value and don't have a value in the registry yet
    const keysToFetchValueFor = Object.keys(metadata).filter(key => metadata[key].isLoadingNewValue && !propertyValueRegistryRef.current[key]);
    for (const key of keysToFetchValueFor) {
      const {
        objectTypeId,
        propertyName,
        objectId
      } = parseIdentifierKey(key);
      propertyValueRegistryRef.current[key] = {
        controlledValue: UNSET,
        aiGeneratedValue: UNSET
      };
      fetchPropertyValue(httpClient, objectTypeId, objectId, propertyName).then(value => {
        if (Object.prototype.hasOwnProperty.call(propertyValueRegistryRef.current, key)) {
          propertyValueRegistryRef.current[key].aiGeneratedValue = value;
          metadataDispatch({
            type: 'finishLoadingNewValue',
            identifierKey: key
          });
        }
      }).catch(() => {
        // Fail open if we fail to fetch a new value to allow users to use the
        // component, ie. set the loading flag to false but drop the value override
        delete propertyValueRegistryRef.current[key];
        metadataDispatch({
          type: 'finishLoadingNewValue',
          identifierKey: key
        });
      });
    }
  }, [httpClient, metadata]);
  useEffect(() => {
    persistProcessingMetadata(metadata);
  }, [metadata]);
  const applyLoadedValue = useCallback((objectTypeId, propertyName, objectId, value) => {
    const key = makeIdentifierKey(objectTypeId, propertyName, objectId);
    const metadataEntry = getOwnProperty(metadata, key);
    let propertyValue = getOwnProperty(propertyValueRegistryRef.current, key);
    if (!metadataEntry || !propertyValue) {
      return value;
    }

    // Side effect: start tracking the controlled value if it's not already
    // being tracked, or drop the override if the tracked controlled value has
    // changed
    if (!metadataEntry.isLoadingNewValue) {
      if (propertyValue.controlledValue === UNSET) {
        propertyValueRegistryRef.current[key].controlledValue = value;
      } else if (propertyValue.controlledValue !== value) {
        delete propertyValueRegistryRef.current[key];
        propertyValue = undefined;
      }
    }
    return propertyValue && !metadataEntry.isLoadingNewValue && propertyValue.aiGeneratedValue !== UNSET ? propertyValue.aiGeneratedValue : value;
  }, [metadata]);
  const startProcessing = useCallback((objectTypeId, propertyName, objectIds) => {
    const identifierKeys = objectIds.map(objectId => makeIdentifierKey(objectTypeId, propertyName, objectId));
    metadataDispatch({
      type: 'startProcessing',
      identifierKeys,
      startedAt: Date.now()
    });
  }, []);
  const value = useMemo(() => ({
    metadata,
    applyLoadedValue,
    startProcessing,
    startPollingAndAttach,
    detachPolling
  }), [applyLoadedValue, detachPolling, metadata, startPollingAndAttach, startProcessing]);
  return /*#__PURE__*/_jsx(AIPropertiesProcessingContext.Provider, {
    value: value,
    children: children
  });
}
export default AIPropertiesProcessingContextProvider;